import { useEffect, useRef, useCallback } from "react";
import { msalResult } from "../../authConfig";
import { useMediaQuery } from "@mui/material";
import TopSectionBackgroundImg from '../../assets/mwa/SignPageBackdrop.jpg';
import { BrandLogo } from "../BrandLogo/brandLogo";
import { Button } from "../Button/button";
import { SignInButton } from "../Button/SignInButton";
import styled from "@emotion/styled";
import { safeString, hasValidString } from "../../utils/stringUtils";

export const deviceSize: any = {
  mobile: 768,
  tablet: 992,
  laptop: 1324,
  desktop: 2024,
}

const { theme } = msalResult;

const BackgroundFilter = styled.div`
  width: 100%;
  height: 100%;
  // background-color: rgba(1, 16, 23, 0.7);
  display: flex;
  flex-direction: column;
`;

const TopSectionInnerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 0%;
  @media screen and (max-width: ${deviceSize.mobile}px) {
    align-items: center;
    margin-top: 3%;
  }
`;

const SloganText = styled.h3`
  margin: 0;
  line-height: 1.4;
  color: #fff;
  font-weight: 500;
  font-size: 45px;
  align-items:left;
  // padding-bottom: 2%;
  @media screen and (max-width: ${deviceSize.mobile}px) {
    font-size: 36px;
    font-weight: 700;
    font-family: ${theme?.Style?.font};
    // padding-bottom: 5%;
  };
  text-transform: capitalize;
  font: normal normal 700 52px/60px ${theme?.Style?.font}
`;

const SloganTextFooter = styled.h3`
  margin: 0;
  line-height: 1.4;
  color: #fff;
  font-weight: 500;
  // margin-left:auto;
  align-items:left;
  padding-bottom: 2%;
  padding-right: 1%;
  padding-left: 1%;
  cursor: pointer;
  @media screen and (max-width: ${deviceSize.mobile}px) {
    font-size: 36px;
    font-weight: 700;
    // padding-bottom: 5%;
  };
  text-transform: capitalize;
  font: normal normal 700 32px/45px ${theme?.Style?.font}
`;


const LazyLoadedBackground = styled.div`
  width: 100%;
  height: 100vh;
  background-size: cover
`;

export const LoginPage = (props: any) => {
  const { children } = props;
  const lazyBackgroundRef = useRef(null);
  const { theme } = msalResult;
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Helper function to create safe URLs
  const getSafeImageUrl = useCallback((baseUrl: unknown, imagePath: unknown, token: unknown, fallbackUrl: string): string => {
    if (hasValidString(baseUrl) && hasValidString(imagePath) && hasValidString(token)) {
      return `url(${safeString(baseUrl)}${safeString(imagePath)}?${safeString(token)})`;
    }
    return `url(${fallbackUrl})`;
  }, []);

  const redirectToWebsite = (url?: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  }

  // Update the intersection observer code
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    // Store ref value in a variable to avoid stale closure in cleanup
    const currentRef = lazyBackgroundRef.current;

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target instanceof HTMLElement) {
          // Set background image with proper typing
          entry.target.style.backgroundImage = getSafeImageUrl(
            theme?.FileAccess?.baseUrl,
            theme?.LandingPage?.BackgroundImage,
            theme?.FileAccess?.sasToken,
            TopSectionBackgroundImg
          );
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, options);

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [theme?.FileAccess?.baseUrl, theme?.FileAccess?.sasToken, theme?.LandingPage?.BackgroundImage, getSafeImageUrl]);

  return (
    <LazyLoadedBackground ref={lazyBackgroundRef}>
      <BackgroundFilter>
        {children}
        <TopSectionInnerContainer>
          <BrandLogo 
            logoSize={isMobile ? 50 : 190} 
            hideLogo={true} 
            logo={
              hasValidString(theme?.FileAccess?.baseUrl) && 
              hasValidString(theme?.LandingPage?.logoUrl) &&
              hasValidString(theme?.FileAccess?.sasToken)
                ? `${safeString(theme?.FileAccess?.baseUrl)}${safeString(theme?.LandingPage?.logoUrl)}?${safeString(theme?.FileAccess?.sasToken)}`
                : undefined
            }
          />
          <LogoContainer>
            <SloganText>{safeString(theme?.LandingPage?.PageTitleMain) || 'Welcome To'}</SloganText>
            <SloganText>{safeString(theme?.LandingPage?.PageTitleSubText) || 'Malthe Winje Portal'}</SloganText>
            <Button bgColor={safeString(theme?.Style?.btnColor) || '#09b885'} size={isMobile ? 2 : 12}>
              <SignInButton name={'Sign in'} />
            </Button>
          </LogoContainer>
          {
            theme?.Footer?.footerLogo ? 
            <BrandLogo logoSize={isMobile ? 50 : 190} hideLogo={true} />
            :
            (
              <div className="foot" style={{ display:'flex', width:'100%', justifyContent:'end'}}>
                <SloganTextFooter>{safeString(theme?.LandingPage?.MWTagline)}</SloganTextFooter>  
                <hr style={{margin:'0px', height:'50%'}} />
                <SloganTextFooter onClick={()=>{redirectToWebsite(safeString(theme?.Footer?.PrivacyPolicy))}}>
                  Privacy Policy
                </SloganTextFooter>
              </div>
            )
          }
        </TopSectionInnerContainer>
      </BackgroundFilter>
    </LazyLoadedBackground>
  )
}
